import { TabButton } from '@/components/TabButton/TabButton';
import noteIcon from '@/public/icons/note.svg';
import relateIcon from '@/public/icons/relateto.svg';
import createIcon from '@/public/icons/create.svg';
import assignIcon from '@/public/icons/assign.svg';
import { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useStore } from '@/stores';
import { INoteState } from '@/pages/Active/Active';
import {
  DynRelatedToData,
  DynFollowUpData,
} from '@/services/dynamics/interfaces/relate-to-info.interface';
import { IRelateTo } from '@/shared/interfaces/relate-to.interface';
import { EEntity } from '@/services/dynamics/enums/entity.enum';
import { Dynamics } from '@/services/dynamics/dynamics';
import { Button } from 'antd';
import { MessageIcon } from '@/components/MessageIcon/MessageIcon';
import acceptIcon from '@/public/icons/accept.svg';
import { NoteBlock } from './NoteBlock.';
import { RelateBlock } from './RelateBlock';
import { CreateBlock } from './CreateBlock';
import { IEntity } from '@/shared/interfaces/entity.interface';
import { AssignBlock } from './AssignBlock';
import { FollowUpBlock } from './FollowUp';
import { SEARCH_DEBOUNCE_MS } from '@/shared/constants/callPage';
import { ISession } from '@/services/sip-js.service';
import './CallPanel.scss';

export const defaultFollowUp: DynFollowUpData = {
  regardingobjectid: null,
  description: '',
  subject: '',
  scheduledstart: '',
  scheduledend: '',
  ownerid: '',
};

interface INoteProps {
  savePhoneCallHandler: (
    note?: INoteState,
    session?: ISession,
    assignedEntity?: IEntity,
  ) => Promise<string>;
  client: IEntity;
}

export const CallPanel = observer(
  ({ savePhoneCallHandler, client }: INoteProps) => {
    const {
      callStore: { callNote: note, setCallNote: setNote },
    } = useStore();
    const [activeTab, setActiveTab] = useState<string>('Note');
    const textArea = useRef(null);
    const [unsavedFormData, setUnsavedFormData] = useState<any>(false);
    const [saving, setSaving] = useState(false);

    const [relatedToData, setRelatedToData] = useState<DynRelatedToData>(
      note.relatedToData,
    );
    const [relatedEntityOptions, setRelatedEntityOptions] = useState([]);
    const [assignToData, setAssignToData] = useState<DynRelatedToData>(
      note.assignToData,
    );
    const [assignEntityOptions, setAssignEntityOptions] = useState([]);
    const [contactOptions, setContactOptions] = useState([]);
    const [followUpData, setFollowUpData] = useState(defaultFollowUp);
    const [currentNote, setCurrentNote] = useState<string>(note.text);

    const relateSearchTimeoutRef = useRef<number | null>(null);
    const assignSearchTimeoutRef = useRef<number | null>(null);

    const isDataSaved = () => {
      return (
        note.text === currentNote &&
        note.relatedToData?.type === relatedToData?.type &&
        note.relatedToData?.id === relatedToData?.id
      );
    };

    const onFollowUpChange = (newData: Partial<DynFollowUpData>) => {
      setFollowUpData({ ...followUpData, ...newData });
    };

    const changeTab = (name: string) => {
      setActiveTab(name);
      if (name === 'Relate to') {
        initDefaultRelateTo();
      }
    };

    const onEntitySelected = (relateTo: IRelateTo) => {
      setRelatedToData({ id: relateTo.value, type: relatedToData.type });
    };

    const onAssignItemSelected = (assignTo: any) => {
      if (assignTo) {
        setAssignToData({ id: assignTo.value, type: assignToData.type });
      } else {
        setAssignToData(assignTo);
      }
    };

    const onFollowUpRelationSelected = (data: any) => {
      setFollowUpData({ ...followUpData, regardingobjectid: data });
    };

    const onSelectEntityType = async (value: EEntity) => {
      setRelatedToData({ id: null, type: value });
      const data = await Dynamics.searchRelateToEntities(value);
      setRelatedEntityOptions(data);
    };

    const onSearchRelateEntities = async (text: string) => {
      if (!relatedToData?.type) return;
      if (relateSearchTimeoutRef.current) {
        window.clearTimeout(relateSearchTimeoutRef.current);
      }
      relateSearchTimeoutRef.current = window.setTimeout(async () => {
        const data = await Dynamics.searchRelateToEntities(
          relatedToData.type,
          text,
        );
        setRelatedEntityOptions(data);
      }, SEARCH_DEBOUNCE_MS);
    };

    const onSearchAssignEntities = async (text: string) => {
      if (!assignToData?.type) return;
      if (assignSearchTimeoutRef.current) {
        window.clearTimeout(assignSearchTimeoutRef.current);
      }
      assignSearchTimeoutRef.current = window.setTimeout(async () => {
        const data = await Dynamics.searchRelateToEntities(
          assignToData.type,
          text,
        );
        setAssignEntityOptions(data);
      }, SEARCH_DEBOUNCE_MS);
    };

    const onSelectAssignType = async (value: EEntity) => {
      setAssignToData({ id: null, type: value });
      const data = value ? await Dynamics.searchRelateToEntities(value) : null;
      setAssignEntityOptions(data);
    };

    const getContactOptions = async () => {
      const data = await Dynamics.searchRelateToEntities(EEntity.CONTACT);
      setContactOptions(data);
    };
    const initDefaultRelateTo = async () => {
      const envStr: any = await Microsoft.CIFramework.getEnvironment();
      const env = JSON.parse(envStr);

      const entityType: EEntity = env?.etn;
      const entityId: string = env?.id;
      if (
        !entityType ||
        !entityId ||
        !(entityType == EEntity.ACCOUNT || entityType == EEntity.CONTACT)
      )
        return;

      setRelatedToData({ id: null, type: entityType });
      const options = await Dynamics.searchRelateToEntities(entityType);

      const option = await Dynamics.getRelateToOption(entityType, entityId);
      if (option) {
        setRelatedToData({ id: option.value, type: entityType });
        options.push(option);
      }
      setRelatedEntityOptions(options);
    };
    useEffect(() => {
      getContactOptions();
    }, []);

    const saveNote = async () => {
      setSaving(true);

      const value = currentNote;
      const recordId = await savePhoneCallHandler({
        text: value,
        relatedToData,
        assignToData,
      });

      setNote({ id: recordId, text: value, relatedToData, assignToData });
      setSaving(false);
    };

    const clearNote = () => {
      setCurrentNote(note.text);
      setRelatedToData(note.relatedToData);
      setAssignToData(note.assignToData);
    };

    const inputEvent = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      const { target } = event;

      setCurrentNote(target.value);
      event.preventDefault();
    };

    const renderContent = () => {
      switch (activeTab) {
        case 'Relate to':
          return (
            <RelateBlock
              relatedToData={relatedToData}
              onSelectEntityType={onSelectEntityType}
              relatedEntityOptions={relatedEntityOptions}
              onEntitySelected={onEntitySelected}
              onSearchEntityOptions={onSearchRelateEntities}
            />
          );
        case 'Create':
          return (
            <CreateBlock
              setUnsavedFormData={setUnsavedFormData}
              data={unsavedFormData}
              client={client}
            />
          );
        case 'Assign to':
          return (
            <AssignBlock
              assignToData={assignToData}
              onSelectAssignType={onSelectAssignType}
              assignEntityOptions={assignEntityOptions}
              onItemSelected={onAssignItemSelected}
              onSearchAssignOptions={onSearchAssignEntities}
              client={client}
              savePhoneCallHandler={savePhoneCallHandler}
            />
          );
        case 'Follow up':
          return (
            <FollowUpBlock
              data={followUpData}
              contactOptions={contactOptions}
              onItemSelected={onFollowUpRelationSelected}
              onChange={onFollowUpChange}
            />
          );
        default:
          return (
            <NoteBlock
              textArea={textArea}
              saving={saving}
              inputEvent={inputEvent}
              currentNote={currentNote}
            />
          );
      }
    };

    return (
      <>
        <div className="panel">
          <TabButton
            testId="set-tab-note"
            icon={noteIcon}
            handleClick={changeTab}
            isActive={activeTab === 'Note'}
            name="Note"
          />
          <TabButton
            testId="set-tab-relate-to"
            icon={relateIcon}
            handleClick={changeTab}
            isActive={activeTab === 'Relate to'}
            name="Relate to"
          />
          <TabButton
            testId="set-tab-create"
            icon={createIcon}
            handleClick={changeTab}
            isActive={activeTab === 'Create'}
            name="Create"
          />
          <TabButton
            testId="set-tab-assign-to"
            icon={assignIcon}
            handleClick={changeTab}
            isActive={activeTab === 'Assign to'}
            name="Assign to"
          />
          <TabButton
            testId="set-tab-follow-up"
            icon={assignIcon}
            handleClick={changeTab}
            isActive={activeTab === 'Follow up'}
            name="Follow up"
          />
        </div>
        <div className="tab-content">{renderContent()}</div>
        {activeTab !== 'Create' &&
          activeTab !== 'Assign to' &&
          activeTab !== 'Follow up' && (
            <div className="note__controls">
              <Button
                loading={saving}
                disabled={isDataSaved()}
                className="note__btn two-btn"
                data-testid="clear-note"
                type="ghost"
                onClick={clearNote}
              >
                Cancel
              </Button>
              <Button
                loading={saving}
                disabled={isDataSaved()}
                className="note__btn  two-btn"
                data-testid="save-note"
                type="primary"
                onClick={saveNote}
                icon={<MessageIcon svgUrl={acceptIcon} />}
              >
                Save
              </Button>
            </div>
          )}
      </>
    );
  },
);
